/**
 * Meta Pixel for the contract flow.
 *
 * Why this exists at all: the ads run on Meta, the visitor lands on the
 * marketing site, and the contract is signed HERE, on another origin. Until
 * now the Pixel only ever saw the lead on savings.club, so Meta was optimising
 * for "signed up for a live" instead of "signed a contract". These events are
 * the difference.
 *
 * The id is the same one the website reports to, on purpose: one pixel is what
 * lets a single Meta account follow the journey end to end. It matches
 * META_PIXEL_ID in the website repo (src/lib/analytics.ts) and the two have to
 * move together.
 *
 * Hardcoded, like GA4, Clarity, Hotjar and GTM already are in this repo. A
 * public number in a build-time env var would cost three files (Dockerfile,
 * production.yaml, staging.yaml) and buy nothing.
 */
const META_PIXEL_ID = '983164502593540';
const META_SCRIPT_ID = 'meta-pixel-loader';

/** The events this app reports. Standard Meta event names, not custom ones, so
 *  the campaigns can optimise against them without extra setup in Ads Manager. */
export type MetaEvent = 'PageView' | 'Lead' | 'CompleteRegistration';

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: unknown;
};

type PixelWindow = Window & { fbq?: Fbq; _fbq?: Fbq };

/**
 * Inject fbevents.js and initialise the pixel. Idempotent: guarded on the
 * script id the same way loadGa4 is, because App.js mounts once but an effect
 * is never a promise that it ran once.
 *
 * Deliberately not calling fbq('track', 'PageView') here. The flow is client
 * routed, so the PageView belongs to whoever knows a screen changed, which is
 * useAnalytics and the four step pages, not the loader.
 */
export const loadMetaPixel = (): void => {
  if (document.getElementById(META_SCRIPT_ID)) return;

  const w = window as PixelWindow;

  if (!w.fbq) {
    const fbq = function (this: unknown, ...args: unknown[]) {
      if (fbq.callMethod) {
        fbq.callMethod.apply(fbq, args);
      } else {
        fbq.queue?.push(args);
      }
    } as Fbq;

    fbq.queue = [];
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';

    w.fbq = fbq;
    if (!w._fbq) w._fbq = fbq;
  }

  const script = document.createElement('script');
  script.id = META_SCRIPT_ID;
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  w.fbq?.('init', META_PIXEL_ID);
};

/**
 * Report an event. A silent no-op when the pixel was never loaded, which is
 * every localhost and staging run, so no call site has to carry an
 * isProduction() check of its own.
 */
export const trackMeta = (event: MetaEvent): void => {
  const { fbq } = window as PixelWindow;
  if (!fbq) return;
  fbq('track', event);
};
